import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tripsApi } from '../../../api/trips.api';
import type { Trip } from '../../../types';
import { useAuthStore } from '../../../stores/auth.store';
import { useDateLocale } from '../../../utils/dateLocale';
import TripGenerationOverlay from '../TripGenerationOverlay';
import WizardShell, { type ShellFooter } from './WizardShell';
import DestinationPicker from '../legs/DestinationPicker';
import LegNightsList from '../legs/LegNightsList';
import DatesStep from './steps/DatesStep';
import TravellersStep from './steps/TravellersStep';
import BookingsStep from './steps/BookingsStep';
import StartStep from './steps/StartStep';
import DocumentsStep from './steps/DocumentsStep';
import StyleStep, { type InterestOption } from './steps/StyleStep';
import SummaryStep, { type RecapRow } from './steps/SummaryStep';
import { useDestinationSearch } from './useDestinationSearch';
import { useDatesAnswer, type DateRange } from './useDatesAnswer';
import { useDocumentImport } from './useDocumentImport';
import { useCreateTrip } from './useCreateTrip';
import { useLegs } from './useLegs';
import ClientPicker, { type PickedClient } from '../../agency/clients/ClientPicker';
import { legDayRanges, shortName } from '../../../utils/tripLegs';
import { ticketCount } from '../../../api/ticketImport';
import { destinationOf, initialForm, wantsAi, maxTravellers, stepSequence, type Step, type StartMode, type TravellerDraft, type WizardForm, type WizardMode } from './types';

interface Props {
  onCreated: (trip: Trip) => void;
  onClose: () => void;
  /** ADR-0033: staff creating a trip or a circuit for their agency. */
  mode?: WizardMode;
}

const PERSONAL: WizardMode = { kind: 'personal' };

let travellerSeq = 0;

function clientTraveller(c: PickedClient): TravellerDraft {
  return { key: c.id, name: c.name, email: c.email ?? '', birthDate: '', showEmail: false, clientId: c.id };
}

export default function NewTripWizard({ onCreated, onClose, mode = PERSONAL }: Props) {
  const { t, i18n } = useTranslation();
  const k = 'dashboard.newTripWizard';
  const user = useAuthStore(s => s.user);
  const dateLocale = useDateLocale();
  const [form, setForm] = useState<WizardForm>(() =>
    initialForm(mode, user?.displayName || user?.username || '', user?.birthDate ?? ''));
  const [step, setStep] = useState<Step>('destination');
  const [interests, setInterests] = useState<{ for: string; options: InterestOption[] } | null>(null);
  const [interestsLoading, setInterestsLoading] = useState(false);
  const [interestsError, setInterestsError] = useState<string | null>(null);

  const agencyId = mode.kind === 'agency' ? mode.agencyId : undefined;
  const docs = useDocumentImport(agencyId);
  const create = useCreateTrip(form, docs, mode, onCreated);
  const sequence = stepSequence(form, mode);
  const index = Math.max(0, sequence.indexOf(step));

  const patch = (p: Partial<WizardForm>) => setForm(f => ({ ...f, ...p }));

  const legs = useLegs(setForm);
  const destination = destinationOf(form);
  const search = useDestinationSearch(form.destinationQuery, step === 'destination', legs.resolve);
  const dates = useDatesAnswer((range: DateRange) => {
    setForm(f => ({ ...f, ...range, legs: legs.fitTo(f, range.nights) }));
    const steps = stepSequence(form, mode);
    go(steps[steps.indexOf('dates') + 1]);
  });

  function addTypedDestination() {
    search.adoptExactMatch();
    legs.commitTyped();
  }

  function submitDestination() {
    addTypedDestination();
    go('dates');
  }

  function go(next: Step) { setStep(next); }
  function goNext() {
    const next = stepSequence(form, mode)[index + 1];
    if (next) go(next);
  }
  function goBack() {
    const prev = sequence[index - 1];
    if (prev) go(prev);
  }

  async function loadInterests() {
    if (interests?.for === destination) return;
    setInterestsLoading(true);
    setInterestsError(null);
    try {
      const { categories } = await tripsApi.suggestInterests(destination, i18n.language);
      setInterests({ for: destination, options: categories });
    } catch {
      setInterestsError(t(`${k}.interestsFailed`));
    } finally {
      setInterestsLoading(false);
    }
  }

  function chooseStart(startMode: StartMode) {
    const next = { ...form, startMode };
    setForm(next);
    const nextSteps = stepSequence(next, mode);
    const following = nextSteps[nextSteps.indexOf('start') + 1];
    if (following === 'style') loadInterests();
    go(following);
  }

  function setTravellerCount(count: number) {
    setForm(f => {
      const n = Math.min(maxTravellers(mode), Math.max(1, count));
      if (n <= f.travellers.length) return { ...f, travellers: f.travellers.slice(0, n) };
      const added: TravellerDraft[] = Array.from({ length: n - f.travellers.length }, (_, i) => ({
        key: `t${++travellerSeq}`,
        name: t(`${k}.travellerDefaultName`, { number: f.travellers.length + i + 1 }),
        birthDate: '', email: '', showEmail: false,
      }));
      return { ...f, travellers: [...f.travellers, ...added] };
    });
  }

  const updateTraveller = (key: string, p: Partial<TravellerDraft>) =>
    setForm(f => ({ ...f, travellers: f.travellers.map(tr => tr.key === key ? { ...tr, ...p } : tr) }));
  const removeTraveller = (key: string) =>
    setForm(f => ({ ...f, travellers: f.travellers.filter(tr => tr.key !== key) }));

  const fmt = (iso: string) => format(new Date(`${iso}T00:00:00`), 'd MMM', { locale: dateLocale });
  const formatRange = (start: string, end: string, nights: number) =>
    t(`${k}.datesAnswer`, { start: fmt(start), end: fmt(end), count: nights });
  const datesValue = form.startDate ? formatRange(form.startDate, form.endDate, form.nights) : '';
  const travellersValue = form.travellers.map(tr => tr.name.trim()).filter(Boolean).join(', ');
  const tickets = ticketCount(form.tickets);
  const ticketsValue = tickets > 0 ? t(`${k}.ticketsCount`, { count: tickets }) : t(`${k}.ticketsNone`);
  const startValue = form.startMode ? t(`${k}.start.${form.startMode}.title`) : '';

  const ranges = legDayRanges(form.legs.map(l => l.nights));
  const legsValue = form.legs.map((l, i) => `${shortName(l.name)} ${t(`${k}.legDaysShort`, { from: ranges[i].from, to: ranges[i].to })}`).join(' · ');
  const legsValid = form.legs.reduce((a, l) => a + l.nights, 0) === form.nights;

  const travellersAnswered = sequence.includes('travellers') && index > sequence.indexOf('travellers');
  const travellersCountKey = mode.kind === 'agency' ? 'agency.wizard.clientsCount' : `${k}.travellersCount`;
  const recap = [destination, datesValue, travellersAnswered ? t(travellersCountKey, { count: form.travellers.length }) : '']
    .filter(Boolean);
  const summaryRows: RecapRow[] = [
    { step: 'destination', label: t(`${k}.stepLabel.destination`), value: destination },
    { step: 'dates', label: t(`${k}.stepLabel.dates`), value: datesValue },
    ...(form.legs.length > 1 ? [{ step: 'legs' as const, label: t(`${k}.stepLabel.legs`), value: legsValue }] : []),
    ...(sequence.includes('travellers') ? [{ step: 'travellers' as const, label: t(`${k}.stepLabel.travellers`), value: travellersValue }] : []),
    ...(sequence.includes('bookings') ? [{ step: 'bookings' as const, label: t(`${k}.stepLabel.bookings`), value: ticketsValue }] : []),
    { step: 'start', label: t(`${k}.stepLabel.start`), value: startValue },
  ];

  // ADR-0033: an agency's trip needs its lead traveller, who owns it.
  const travellersValid = mode.kind === 'agency'
    ? form.travellers.length > 0
    : form.travellers.every(tr => !tr.email.trim() || /^\S+@\S+\.\S+$/.test(tr.email.trim()));
  const back = index > 0 ? goBack : undefined;

  if (create.generation.phase !== 'idle') {
    const g = create.generation;
    return (
      <WizardShell current={sequence.length} total={sequence.length} eyebrow={destination}
        title={t(`${k}.generatingTitle`)} recap={[]} footer={null} onClose={onClose}>
        <TripGenerationOverlay
          phase={g.phase} destination={destination} nights={form.nights}
          venueNames={g.venueNames} days={g.days} tipCount={g.tipCount} error={g.error}
          showTips={form.startMode === 'ai' || form.complementWithAi}
          ticketNames={g.ticketNames} unplacedTickets={g.unplacedTickets}
          showTickets={tickets > 0} showSuggestions={wantsAi(form) || !!docs.plan}
          onDone={() => create.createdTrip && onCreated(create.createdTrip)}
        />
      </WizardShell>
    );
  }

  const screens: Record<Step, { footer: ShellFooter; content: ReactElement }> = {
    destination: {
      footer: { onNext: submitDestination, nextDisabled: form.legs.length === 0 && !form.destinationQuery.trim() },
      content: (
        <DestinationPicker legs={form.legs} onRemove={legs.remove}
          value={form.destinationQuery} onChange={v => patch({ destinationQuery: v })}
          predictions={search.predictions} onSelect={search.select} onAddTyped={addTypedDestination} onSubmit={submitDestination} />
      ),
    },
    dates: {
      footer: { onBack: back, onNext: dates.submit, nextDisabled: !dates.canSubmit, nextLoading: dates.loading },
      content: <DatesStep dates={dates} formatRange={formatRange} />,
    },
    legs: {
      footer: { onBack: back, onNext: goNext, nextDisabled: !legsValid },
      content: <LegNightsList legs={form.legs} total={form.nights} onNights={legs.setNights} />,
    },
    travellers: {
      footer: { onBack: back, onNext: goNext, nextDisabled: !travellersValid },
      content: mode.kind === 'agency' ? (
        <ClientPicker agencyId={mode.agencyId} max={maxTravellers(mode)}
          picked={form.travellers.map(tr => ({ id: tr.clientId ?? tr.key, name: tr.name, email: tr.email || null }))}
          onChange={picked => patch({ travellers: picked.map(clientTraveller) })} />
      ) : (
        <TravellersStep travellers={form.travellers} max={maxTravellers(mode)}
          onCountChange={setTravellerCount} onUpdate={updateTraveller} onRemove={removeTraveller} />
      ),
    },
    bookings: {
      footer: { onBack: back, onNext: goNext, nextLabel: tickets === 0 ? t(`${k}.ticketsSkip`) : undefined },
      content: <BookingsStep tickets={form.tickets} onChange={next => patch({ tickets: next })} />,
    },
    start: {
      footer: { onBack: back, onNext: form.startMode ? () => chooseStart(form.startMode!) : undefined },
      content: <StartStep value={form.startMode} onChoose={chooseStart} />,
    },
    documents: {
      footer: {
        onBack: back,
        onNext: docs.staged.length > 0 && !docs.plan ? docs.analyze : () => { if (form.complementWithAi) loadInterests(); goNext(); },
        nextLabel: docs.staged.length > 0 && !docs.plan ? t('import.analyze', { count: docs.staged.length }) : undefined,
        nextDisabled: docs.importing || (!docs.plan && docs.staged.length === 0 && !form.complementWithAi),
        nextLoading: docs.importing,
      },
      content: <DocumentsStep docs={docs} complementWithAi={form.complementWithAi} onComplementChange={v => patch({ complementWithAi: v })} />,
    },
    style: {
      footer: { onBack: back, onNext: goNext, nextDisabled: interestsLoading },
      content: (
        <StyleStep
          pace={form.pace} budget={form.budget} notes={form.notes}
          interests={interests?.options ?? []} interestsLoading={interestsLoading} interestsError={interestsError}
          selectedInterests={form.selectedInterests}
          onPace={pace => patch({ pace })} onBudget={budget => patch({ budget })} onNotes={notes => patch({ notes })}
          onToggleInterest={label => setForm(f => ({
            ...f,
            selectedInterests: f.selectedInterests.includes(label)
              ? f.selectedInterests.filter(i => i !== label)
              : [...f.selectedInterests, label],
          }))}
        />
      ),
    },
    summary: {
      footer: { onBack: back, onNext: create.submit, nextLabel: t(`${k}.create`), nextDisabled: !form.name.trim(), nextLoading: create.loading },
      content: (
        <SummaryStep rows={summaryRows} onEdit={go}
          name={form.name} onName={name => patch({ name, nameEdited: true })}
          currency={form.budgetCurrency} onCurrency={budgetCurrency => patch({ budgetCurrency })} />
      ),
    },
  };

  const screen = screens[step];
  // ADR-0037: an agency isn't asked who's coming, but which of its clients the trip is for.
  const agencyAsk = mode.kind === 'agency' && step === 'travellers';
  return (
    <WizardShell
      current={index + 1} total={sequence.length}
      eyebrow={t(`${k}.stepLabel.${step}`)}
      title={agencyAsk ? t('agency.wizard.ask') : t(`${k}.ask.${step}`, { destination: form.legs.map(l => shortName(l.name)).join(' · ') })}
      subtitle={agencyAsk ? t('agency.wizard.hint') : t(`${k}.hint.${step}`, { defaultValue: '' }) || undefined}
      recap={step === 'summary' ? [] : recap} footer={screen.footer} onClose={onClose}
    >
      {screen.content}
    </WizardShell>
  );
}
